/**
 * components/workspace/WorkspaceAcknowledgement.jsx
 *
 * RC 1.58 Phase 3E (operator-locked, 2026-02-14) — Workspace
 * Session Guidance modal. Supersedes the Phase 4 "before you open
 * the workspace" copy.
 *
 * Operator-locked contract:
 *   • Triggered ONLY at two entry surfaces:
 *       1. Before "Analyse Fit" begins.
 *       2. Before "Open Workspace" routes the user in.
 *     No re-rendering on rerun / retry / regenerate buttons, no
 *     mounting inside workspace sections.
 *   • Title: "Workspace Session Guidance".
 *   • Primary button: "Continue". Secondary button: "Cancel".
 *   • Acknowledgement is stored in `sessionStorage` under
 *     `workspace:ack:shown` so the modal does NOT re-appear in the
 *     same browser tab session — neither at the second entry
 *     surface nor on subsequent visits. A new tab / new browser
 *     session shows it again.
 *   • Tab switching / section navigation INSIDE the workspace is
 *     NEVER blocked. The gate fires only at the entry boundary.
 *   • No backend / billing / scoring / orchestration / session-
 *     guard / generation change. Pure UI.
 *
 * Tone: calm, professional, informational. No "double charges", no
 * "system is broken" language.
 *
 * Usage:
 *
 *     import {
 *       useWorkspaceAcknowledgement,
 *       WorkspaceAcknowledgementDialog,
 *     } from '@/components/workspace/WorkspaceAcknowledgement';
 *
 *     const {
 *       guardedNavigate,
 *       guardedAction,
 *       dialogProps,
 *     } = useWorkspaceAcknowledgement();
 *
 *     // Route-based entry (Open Workspace):
 *     guardedNavigate(`/applications/workspace/${jobId}`);
 *
 *     // Action-based entry (Analyse Fit):
 *     guardedAction(handleAnalyzeFit);
 *
 *     // Render once near the top of the page:
 *     <WorkspaceAcknowledgementDialog {...dialogProps} />
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const ACK_STORAGE_KEY = 'workspace:ack:shown';

function hasAcknowledged() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(ACK_STORAGE_KEY) === '1';
  } catch {
    // sessionStorage blocked (private mode, etc.) — fall through
    // and just show the modal. Better UX than crashing.
    return false;
  }
}

function markAcknowledged() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ACK_STORAGE_KEY, '1');
  } catch {
    /* fail-open */
  }
}

/**
 * useWorkspaceAcknowledgement
 *
 * Returns `{ guardedNavigate, guardedAction, dialogProps }`.
 *
 * `guardedNavigate(path)` — replacement for `navigate(path)`. On the
 * first call within a browser session it opens the acknowledgement
 * dialog and defers the actual `navigate(...)` until the user
 * clicks "Continue". On subsequent calls within the same session
 * it routes immediately.
 *
 * `guardedAction(callback)` — same gate, but for non-route entry
 * points (e.g. the "Analyse Fit" button). On the first call within
 * a browser session it opens the dialog and defers the callback
 * until the user clicks "Continue"; on subsequent calls it invokes
 * `callback()` immediately.
 *
 * `dialogProps` — spread onto `<WorkspaceAcknowledgementDialog />`
 * to render the modal.
 */
export function useWorkspaceAcknowledgement() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const guardedNavigate = useCallback(
    (path) => {
      if (!path) return;
      if (hasAcknowledged()) {
        navigate(path);
        return;
      }
      setPendingPath(path);
      setPendingAction(null);
      setOpen(true);
    },
    [navigate],
  );

  const guardedAction = useCallback(
    (callback) => {
      if (typeof callback !== 'function') return;
      if (hasAcknowledged()) {
        callback();
        return;
      }
      // Wrap in an object so React's state updater doesn't try to
      // call the function as `prev => next` (which would invoke the
      // user callback synchronously and discard its return value).
      setPendingAction(() => callback);
      setPendingPath(null);
      setOpen(true);
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    markAcknowledged();
    setOpen(false);
    const targetPath = pendingPath;
    const targetAction = pendingAction;
    setPendingPath(null);
    setPendingAction(null);
    if (targetPath) navigate(targetPath);
    else if (targetAction) targetAction();
  }, [navigate, pendingPath, pendingAction]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setPendingPath(null);
    setPendingAction(null);
  }, []);

  return {
    guardedNavigate,
    guardedAction,
    dialogProps: {
      open,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
}

/**
 * WorkspaceAcknowledgementDialog
 *
 * Operator-locked copy + tone. Calm and informational. Two
 * actions: Cancel (keeps the user where they are) and the primary
 * confirm "Continue".
 *
 * `intent` selects the body copy variant. Title and buttons are
 * shared. Defaults to `'workspace'` for the Open Workspace entry
 * surface. Phase 3E.A (operator-locked, 2026-02-14) adds the
 * lighter `'analyse_fit'` variant used by the Analyse Fit click
 * gate on JobDetailPage — Analyse Fit rebinds cleanly via the
 * existing URL/result recovery behaviour, so its copy is shorter
 * and does not warn against post-completion refreshes.
 */
export function WorkspaceAcknowledgementDialog({
  open,
  onConfirm,
  onCancel,
  intent = 'workspace',
}) {
  const isAnalyseFit = intent === 'analyse_fit';
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) onCancel(); }}
    >
      <DialogContent
        data-testid="workspace-ack-dialog"
        data-intent={intent}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle data-testid="workspace-ack-title">
            Workspace Session Guidance
          </DialogTitle>
          <DialogDescription
            data-testid="workspace-ack-description"
            className="pt-1"
          >
            A few quick notes so your session runs smoothly.
          </DialogDescription>
        </DialogHeader>

        {isAnalyseFit ? (
          <div
            className="space-y-3 text-sm text-ndor-ink-muted leading-relaxed"
            data-testid="workspace-ack-body"
            data-variant="analyse_fit"
          >
            <p data-testid="workspace-ack-af-body-line-1">
              Analyse Fit may take a short time to complete.
            </p>
            <p data-testid="workspace-ack-af-body-line-2">
              You may switch tabs, move to other windows, or navigate
              within the platform while analysis is running.
            </p>
            <p data-testid="workspace-ack-af-body-line-3">
              Please avoid refreshing the page while analysis is
              still in progress.
            </p>
            <p data-testid="workspace-ack-af-body-line-4">
              Once analysis completes, refreshing or revisiting the
              page is safe and results will reconnect automatically.
            </p>
          </div>
        ) : (
          <div
            className="space-y-3 text-sm text-ndor-ink-muted leading-relaxed"
            data-testid="workspace-ack-body"
            data-variant="workspace"
          >
            <p data-testid="workspace-ack-body-line-1">
              You may switch tabs or move between workspace sections
              safely while generation is running.
            </p>
            <p data-testid="workspace-ack-body-line-2">
              Please do not refresh or close this page until generation
              fully completes.
            </p>
            <p data-testid="workspace-ack-body-line-3">
              If generation appears complete but results do not
              immediately appear, move to another workspace
              tab/section and return before attempting another run. A
              short refresh delay may occur while results reconnect.
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            data-testid="workspace-ack-cancel-button"
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onConfirm}
            data-testid="workspace-ack-confirm-button"
            className="w-full sm:w-auto"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
